import type { IDomainValidForSegments } from "app/pages/segments/wizard/SegmentFirstStep/SegmentCreationFirstStep";
import type { ICustomSegment as ICustomSegmentAPI } from "app/services/segments/segmentsApiService";
import { ESegmentShareState, ESegmentShareStateToServer } from "app/services/segments/segmentsApiService";
import { getHasSegmentShareClaim } from "app/services/segments/SegmentsUtils";
import type {
    IWordsFilterConfig,
    ISegmentRuleItem,
    ISegmentsUrlParams,
    ICustomSegment,
    ITrafficData,
} from "app/services/segments/segmentsWizardServiceTypes";
import type {
    IPopularSegments,
    IWordPredictions,
} from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";

import { DefaultFetchService } from "../fetchService";

// @ts-ignore
// eslint-disable-next-line import/default
import Worker from "./segmentsWizardService.worker.js";
import type { IWorkerProxyMethod } from "./workerProcessor";
import WorkerProcessor from "./workerProcessor";

const DEFAULT_WEB_SOURCE = "Desktop";
const DEFAULT_PAGE_SIZE_SEGMENTS = 100000;
const DEFAULT_PAGE_SIZE = 100;

export const DEFAULT_WORDS_FILTER_CONFIGS = {
    wordsOnly: {
        wordMinLength: 2,
        wordMaxShare: 0.7,
        allowDigits: false,
        allowBlacklisted: false,
    },
    noRobotsTxt: {
        // wordMinCount: 2,
        wordMinShareByCount: 2,
    },
};

export default class SegmentsApiService {
    protected fetchService: DefaultFetchService;

    // hold data for the segments
    protected loadedParamsQueryIdentifier: string | undefined;
    protected segmentsHeader:
        | {
              MainSite: string;
          }
        | undefined;

    // hold data for the worker
    protected workerProcessor: WorkerProcessor;

    constructor() {
        this.fetchService = DefaultFetchService.getInstance();
        this.workerProcessor = new WorkerProcessor();
    }

    // stops the worker - make sure to stop the worker when ending usage with this service
    public stopWorker() {
        if (this.workerProcessor.isStarted()) {
            this.workerProcessor.stop();
            this.loadedParamsQueryIdentifier = "";
        }
    }

    public isDomainValidForSegments(params: { keys: string[] }): Promise<IDomainValidForSegments[]> {
        return this.fetchService.get("api/websiteoverview/IsDomainValidForSegments", params);
    }

    public getUserCustomSegments(): Promise<any[]> {
        return this.fetchService.get("/api/userdata/segments/customsegments");
        // return Promise.resolve(Object.values(segmentsMock)); //
    }

    public hasExistingUrlsCompundStringNoRobots(params: ISegmentsUrlParams, rules: ISegmentRuleItem[]): Promise<any> {
        const queryString = this.prepareParams(params);
        return this.fetchService.post(`widgetApi/PopularSegments/CustomMatch?${queryString}`, {
            rules,
        });
    }

    public getCustomSegmentMetaData(params: { sid: string }): Promise<ICustomSegment> {
        return this.fetchService.get(`/api/userdata/segments/customsegments/${params.sid}`);
        // return new Promise(resolve => setTimeout(resolve.bind(null, segmentMetaData), 1500));
    }

    public getRulesHighlightWords(rules: ISegmentRuleItem[]): string[] {
        return rules?.reduce<string[]>((acc, rule) => [...acc, ...(rule.type === 0 ? rule.words : [])], []) ?? [];
    }

    public getRulesExactMatchPhrases(rules: ISegmentRuleItem[]): string[] {
        return (
            rules?.reduce<string[]>((acc, rule) => [...acc, ...(rule.type === 0 ? (rule.exact ?? []) : [])], []) ?? []
        );
    }

    // loads the segments data, prepares them, and syncs with worker (caches data on worker)
    public async loadPrepareSegments(params: ISegmentsUrlParams, isRaw: boolean, doSync = true) {
        const paramsQueryIdentifier = this.prepareParams(params, DEFAULT_PAGE_SIZE_SEGMENTS);
        if (!doSync || paramsQueryIdentifier !== this.loadedParamsQueryIdentifier) {
            if (doSync) {
                this.loadedParamsQueryIdentifier = paramsQueryIdentifier;
            }
            // fetch the segments from server
            const data = await this.fetchService.post<{
                Data: Array<[string, number]>;
                Header: { MainSite: string };
            }>(`widgetApi/PopularSegments/PopularSegmentPages/PopularPages?${paramsQueryIdentifier}`, { rules: [] });

            // only cache if the params had not changed since was called
            if (!doSync || paramsQueryIdentifier === this.loadedParamsQueryIdentifier) {
                const allSegmentUrls = await this.runWorker("prepareSegments")(
                    data.Data,
                    data.Header.MainSite,
                    isRaw,
                    doSync,
                );
                if (!doSync) {
                    return { allSegmentUrls, segmentsHeader: data.Header };
                }
                this.segmentsHeader = data.Header;
                return true;
            }
            return false;
        }
        return true;
    }

    public async getWordPredictions(
        rules: ISegmentRuleItem[],
        wordsFilterConfig?: IWordsFilterConfig,
    ): Promise<IWordPredictions> {
        const usedWordsSet = rules.reduce((acc, rule) => {
            rule.words.forEach((w) => acc.add(w));
            rule.exact?.forEach((e) => acc.add(e));
            return acc;
        }, new Set());

        const wordPredictions = await this.runWorker("getKeywordPredictionsList")(
            rules,
            usedWordsSet,
            wordsFilterConfig,
        );

        return {
            Data: wordPredictions,
            TotalCount: wordPredictions.length,
            Header: this.segmentsHeader,
        } as IWordPredictions;
    }

    public async getFolderPredictions(rules: ISegmentRuleItem[]): Promise<IWordPredictions> {
        const folderPredictions = await this.runWorker("getFolderPredictionsList")(rules);

        return {
            Data: folderPredictions,
            TotalCount: folderPredictions?.length,
            Header: this.segmentsHeader,
        } as IWordPredictions;
    }

    public async getPopularPages(rules: ISegmentRuleItem[], selectedSite: ISite): Promise<IPopularSegments> {
        const filteredSegments = await this.runWorker("searchSegments")(rules);
        return {
            Data: filteredSegments.map((sg: { URL: string; Share: number }) => ({
                Page: {
                    Highlights: [{}],
                    URL: sg.URL,
                },
                Share: sg.Share,
            })),
            TotalCount: filteredSegments.length,
            Header: {
                MainSite: selectedSite.name,
            },
        } as IPopularSegments;
    }

    public getPopularPagesDiff(
        oldRules: ISegmentRuleItem[],
        newRules: ISegmentRuleItem[],
        remove: boolean,
        params: ISegmentsUrlParams,
    ): Promise<IPopularSegments> {
        const queryString = this.prepareParams(params);
        return this.fetchService.post(
            `/widgetApi/PopularSegments/PopularSegmentPages/PopularPagesDiff?${queryString}`,
            {
                remove,
                oldRules,
                newRules,
            },
        );
    }

    public calculateSegmentShare(domain: string, country: number, rules: ISegmentRuleItem[]): Promise<ITrafficData> {
        const queryString = `keys=${domain}&country=${country}`;

        return this.fetchService.post(
            `api/userdata/segments/calculateshare?${queryString}`,
            {
                rules,
            },
            { withFullResponse: true },
        );
    }

    public createCustomSegment(customSegment: ICustomSegment): Promise<ICustomSegmentAPI> {
        const sharingState = getHasSegmentShareClaim()
            ? ESegmentShareStateToServer.PRIVATE
            : ESegmentShareStateToServer.PUBLIC;
        return this.fetchService.post("/api/userdata/segments/customsegments", {
            ...customSegment,
            targetType: sharingState,
        });
    }

    public updateCustomSegment(customSegment: ICustomSegment): Promise<any> {
        let formattedSharingState;
        switch (customSegment.targetType) {
            case ESegmentShareState.PUBLIC:
                formattedSharingState = ESegmentShareStateToServer.PUBLIC;
                break;
            case ESegmentShareState.PRIVATE:
                formattedSharingState = ESegmentShareStateToServer.PRIVATE;
                break;
            case ESegmentShareState.RESTRICTED:
                formattedSharingState = ESegmentShareStateToServer.RESTRICTED;
                break;
        }
        return this.fetchService.put("/api/userdata/segments/customsegments", {
            ...customSegment,
            targetType: formattedSharingState,
        });
    }

    public deleteCustomSegment(segmentId: string): Promise<any> {
        return this.fetchService.delete(`/api/userdata/segments/customsegments/${segmentId}`);
    }

    protected runWorker(name: string): IWorkerProxyMethod {
        if (!this.workerProcessor.isStarted()) {
            this.workerProcessor.start(Worker);
        }
        return this.workerProcessor.run(name);
    }

    private prepareParams(params: ISegmentsUrlParams, pageSize: number = DEFAULT_PAGE_SIZE): string {
        return (
            `from=${params.from}&to=${params.to}&includeSubDomains=${params.includeSubDomains}&keys=${params.keys}` +
            `&orderBy=${params.orderBy}&isWindow=${params.isWindow}&timeGranularity=${params.timeGranularity}` +
            `&PageSize=${pageSize}&webSource=${DEFAULT_WEB_SOURCE}`
        );
    }
}
